import React, { createContext, useContext, useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig, loginRequest } from "./authConfig";

// Create the MSAL PublicClientApplication instance once outside the component
// This ensures it's a singleton.
const msalInstance = new PublicClientApplication(msalConfig);

// Eagerly initialize the MSAL instance here, outside of any React lifecycle.
// This ensures the instance is fully ready before any component tries to use it.
msalInstance.initialize().then(() => {
  console.log("MSAL instance eagerly initialized successfully.");
}).catch(error => {
  console.error("MSAL eager initialization failed:", error);
  // Log this error, but the application might still try to proceed if possible.
  // The isLoadingAuth state will eventually handle the UI.
});

// Define a default context value to prevent destructuring errors if used before provider is ready
const defaultAuthContext = {
  account: null,
  signIn: () => {},
  signOut: () => {},
  email: null,
  username: null,
  isLoadingAuth: true, // Default to true while authentication is being set up
};

const AuthContext = createContext(defaultAuthContext); // Provide a default value to the context

export const AuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [email, setEmail] = useState(sessionStorage.getItem("email") || null);
  const [username, setUsername] = useState(sessionStorage.getItem("username") || null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true); // New state for overall auth loading

  useEffect(() => {
    const handleMsalRedirectAndAccounts = async () => {
      if (window.__E2E_TEST__) {
        setAccount({ name: 'Tech', username: 'Tech@eyantra.com' });
        setEmail('Tech@eyantra.com');
        setUsername('Tech');
        setIsLoadingAuth(false);
        return;
      }

      try {
        // Handle redirect promises (e.g., after login or acquireTokenRedirect)
        const response = await msalInstance.handleRedirectPromise();

        if (response && response.account) {
          const { account } = response;
          setAccount(account);
          setEmail(account.username);
          setUsername(account.name);
          sessionStorage.setItem("email", account.username);
          sessionStorage.setItem("username", account.name);
        } else {
          // If no redirect response, check for existing accounts (e.g., after page refresh)
          const currentAccounts = msalInstance.getAllAccounts();
          if (currentAccounts.length === 1) {
            const account = currentAccounts[0];
            setAccount(account);
            setEmail(account.username);
            setUsername(account.name);
            sessionStorage.setItem("email", account.username);
            sessionStorage.setItem("username", account.name);
          } else {
            // No account found, clear any stale session storage
            setAccount(null);
            setEmail(null);
            setUsername(null);
            sessionStorage.removeItem("email");
            sessionStorage.removeItem("username");
          }
        }
      } catch (error) {
        console.error("MSAL redirect handling or account check error:", error);
        // Even on error, ensure loading state is false so UI can proceed or show error
        setAccount(null);
        setEmail(null);
        setUsername(null);
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("username");
      } finally {
        setIsLoadingAuth(false); // Auth loading is complete regardless of success or failure
      }
    };

    // Run this effect once on mount to handle authentication flow
    handleMsalRedirectAndAccounts();

    // Effect to handle account changes (e.g., from silent token renewal or logout)
    // This relies on MSAL's internal event system.
    const handleAccountChange = (event) => {
      if (window.__E2E_TEST__) return;
      if (event.eventType === 'ACCOUNT_ADDED' || event.eventType === 'ACCOUNT_REMOVED') {
        const currentAccounts = msalInstance.getAllAccounts();
        if (currentAccounts.length === 1) {
          const acc = currentAccounts[0];
          setAccount(acc);
          setEmail(acc.username);
          setUsername(acc.name);
          sessionStorage.setItem("email", acc.username);
          sessionStorage.setItem("username", acc.name);
        } else if (currentAccounts.length === 0) {
          setAccount(null);
          setEmail(null);
          setUsername(null);
          sessionStorage.removeItem("email");
          sessionStorage.removeItem("username");
        }
      }
    };

    // Subscribe to MSAL events
    const callbackId = msalInstance.addEventCallback(handleAccountChange);

    // Cleanup subscription on unmount
    return () => {
      msalInstance.removeEventCallback(callbackId);
    };

  }, []); // Empty dependency array means this effect runs once on mount


  const signIn = () => {
    msalInstance.loginRedirect(loginRequest);
  };

  const signOut = () => {
    msalInstance.logoutRedirect();
    setEmail(null);
    setUsername(null);
    setAccount(null);
    sessionStorage.removeItem("email");
    sessionStorage.removeItem("username");
  };

  // The value provided to the AuthContext
  const authContextValue = { account, signIn, signOut, email, username, isLoadingAuth };

  return (
    // MsalProvider makes the msalInstance available to useMsal hook in child components
    <MsalProvider instance={msalInstance}>
      {/* AuthContext.Provider provides custom auth state to useAuth hook */}
      <AuthContext.Provider value={authContextValue}>
        {children}
      </AuthContext.Provider>
    </MsalProvider>
  );
};

export const useAuth = () => useContext(AuthContext);
